'use client';

import Link from 'next/link';
import Image from 'next/image';
import { useEffect, useRef, useState } from 'react';

import { Menu as MenuType } from '../menu/types';
import MenuLink from '../link/MenuLink';
import MobileFFBBLogo from '../../../../public/svg/logo_mobile.svg';
import MobileBurgerMenu from '../../../../public/svg/mobile_burger_menu.svg';
import { MenuFC } from '../menu/Menu';
import { SearchMenu } from '../menu/SearchMenu';
import { LiveMenu } from '../menu/LiveMenu';
import { FindMenu } from '../menu/FindMenu';

interface Props {
  topMenus: Array<MenuType>;
  mainMenus: Array<MenuType>;
}

export default function MobileMenu({ topMenus, mainMenus }: Props) {
  const drawerCheckbox = useRef<HTMLInputElement>(null);
  const [lastScrollTop, setLastScrollTop] = useState(0);
  const [isScrollingUp, setIsScrollingUp] = useState(true);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isChecked, setIsChecked] = useState(false);
  const detailsRef = useRef<HTMLDetailsElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const updateMenuPosition = (newPosition: number) => {
    if (menuRef.current) {
      menuRef.current.style.top = `${newPosition}px`;
    }
  };

  useEffect(() => {
    const st = window.scrollY;
    if (st > 65) {
      updateMenuPosition(0);
    }
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const st = window.pageYOffset || document.documentElement.scrollTop;

      const scrollingUp = st < lastScrollTop;
      const newPosition = st > 1 && st < 36 ? 36 - st : 0;

      if (scrollingUp) {
        if (st < 36) {
          updateMenuPosition(37);
        } else {
          updateMenuPosition(newPosition);
        }
      } else {
        if (st > 45) {
          updateMenuPosition(-100);
        }
      }
      setIsScrolled(st > 65);
      setIsScrollingUp(scrollingUp);
      setLastScrollTop(st);
    };

    const st = window.pageYOffset || document.documentElement.scrollTop;

    setLastScrollTop(st <= 0 ? 0 : st);
    setIsScrolled(st > 65);

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollTop]);

  const handleCheckboxChange = (event: {
    target: { checked: boolean | ((prevState: boolean) => boolean) };
  }) => {
    setIsChecked(event.target.checked);
  };

  const hideSideDrawer = () => {
    if (isChecked) {
      drawerCheckbox.current?.click();
    }
  };

  return (
    <div>
      <div className="flex h-[36px] bg-indigo-950 text-sm font-bold lg:hidden">
        <div className="grow">
          <ul className="w-46 menu relative z-20 h-[36px] bg-indigo-950 p-0 text-white">
            <li className="bg-indigo-950">
              <details ref={detailsRef}>
                <summary className="uppercase">Univers FFBB</summary>
                <ul>
                  <MenuFC
                    menuItems={topMenus}
                    type="top"
                    onClick={() => {
                      detailsRef.current?.removeAttribute('open');
                    }}
                  />
                </ul>
              </details>
            </li>
          </ul>
        </div>
        <div className="z-20 flex">
          <Link
            href={'/je-signale'}
            className={`group flex w-fit items-center justify-center bg-gray-500 px-6 text-white`}
            tabIndex={0}
            prefetch={false}
          >
            Je signale
          </Link>
          <LiveMenu
            styles="text-white"
            menu={{ label: 'Live', url: { url: '/live', external: false }, children: [] }}
          />
        </div>
      </div>
      <div className="drawer drawer-end lg:hidden">
        <input
          id="mobile-drawer"
          type="checkbox"
          className="drawer-toggle"
          onChange={handleCheckboxChange}
          ref={drawerCheckbox}
        />
        <div className="drawer-content flex flex-col">
          <div
            ref={menuRef}
            className={`fixed z-10 flex min-h-16 w-full items-center border-b bg-white transition-all duration-1000 ease-in-out ${isScrollingUp ? `` : '-top-full'}`}
          >
            <div className="grow pl-3.5">
              <Link href={'/'} prefetch={false}>
                <Image src={MobileFFBBLogo} alt="Accueil" />
              </Link>
            </div>

            <div className="relative mr-2 flex items-center gap-2">
              <div className="flex h-[45px] w-[45px] justify-center rounded-full border border-zinc-200">
                <SearchMenu showInput={false} callback={hideSideDrawer} />
              </div>
              <div className="flex h-[45px] w-[45px] justify-center rounded-full border border-zinc-200">
                <FindMenu
                  styles="flex items-center h-full text-xs"
                  menu={{
                    label: 'Trouver',
                    url: { external: false, url: '/trouver', slug: '' },
                    children: [],
                  }}
                />
              </div>
              <div className="flex">
                <label
                  htmlFor="mobile-drawer"
                  aria-label="open sidebar"
                  className="mobile-close-open-button-gradient"
                >
                  {isChecked ? (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      className="inline-block h-6 w-6 stroke-current"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M6 18L18 6M6 6l12 12"
                      ></path>
                    </svg>
                  ) : (
                    <Image src={MobileBurgerMenu} alt="Ouvrir menu" />
                  )}
                </label>
              </div>
            </div>
          </div>
        </div>
        <div className={`drawer-side z-10`} style={{ top: isScrolled ? '64px' : '100px' }}>
          <div className={'min-h-full w-full justify-end bg-white lg:hidden'}>
            <div>
              <div className="join join-vertical w-full">
                {mainMenus.map((menu, index) => {
                  const subMenus = menu.children.map((subMenu, index) => {
                    return (
                      <MenuLink
                        key={index}
                        prefetch={false}
                        label={subMenu.label}
                        url={subMenu.url}
                        styles="normal-case text-sm"
                        onClick={hideSideDrawer}
                      />
                    );
                  });

                  if (index === 0) {
                    return (
                      <Link
                        key={index}
                        href={menu.url.url + (menu.url.slug ? `/${menu.url.slug}` : '')}
                        prefetch={false}
                        className="p-4 font-AgencyFBBlackComp text-3xl font-thin uppercase leading-6 tracking-wide text-slate-950 antialiased"
                        onClick={hideSideDrawer}
                      >
                        {menu.label}
                      </Link>
                    );
                  } else {
                    return (
                      <div
                        key={index}
                        className={`collapse join-item ${index === 0 ? '' : 'collapse-arrow'} border-b border-gray-200`}
                      >
                        <input type="radio" name="mobile-menu-accordion" />
                        <div className="collapse-title font-AgencyFBBlackComp text-3xl font-thin uppercase tracking-wide text-slate-950 antialiased">
                          {index > 0 && <>{menu.label}</>}
                        </div>
                        {index > 0 && (
                          <div key={index} className="collapse-content flex flex-col gap-4">
                            {subMenus}
                          </div>
                        )}
                      </div>
                    );
                  }
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
